type AppointmentParty = Record<string, unknown> | string | null | undefined;

export interface AppointmentUpdateEvent {
  appointmentId?: string;
  status?: string;
  previousStatus?: string | null;
  appointment?: {
    _id?: string;
    id?: string;
    status?: string;
    dateTime?: string;
    client?: AppointmentParty;
    stylist?: AppointmentParty;
    service?: AppointmentParty;
    [key: string]: unknown;
  };
}

function appointmentKey(value: { _id?: string; id?: string } | null | undefined): string {
  if (!value) return '';
  return String(value._id || value.id || '');
}

function mergeParty(existing: AppointmentParty, incoming: AppointmentParty): AppointmentParty {
  if (incoming == null) return existing;
  if (typeof incoming !== 'object') return existing ?? incoming;
  if (!existing || typeof existing !== 'object') return incoming;
  return { ...existing, ...incoming };
}

export function mergeAppointmentRecord<T extends {
  client?: AppointmentParty;
  stylist?: AppointmentParty;
  service?: AppointmentParty;
  status?: string;
}>(
  existing: T | null | undefined,
  incoming: Partial<T> | null | undefined,
  statusFallback?: string
): T {
  const base = existing ? { ...existing } : ({} as T);
  const next = { ...base, ...(incoming || {}) } as T;
  next.client = mergeParty(existing?.client, incoming?.client) as T['client'];
  next.stylist = mergeParty(existing?.stylist, incoming?.stylist) as T['stylist'];
  next.service = mergeParty(existing?.service, incoming?.service) as T['service'];
  const status = incoming?.status || statusFallback;
  if (status) next.status = status as T['status'];
  return next;
}

export function eventAppointmentId(event: AppointmentUpdateEvent | null | undefined): string {
  if (!event) return '';
  return String(event.appointmentId || event.appointment?._id || event.appointment?.id || '');
}

export function mergeAppointmentUpdate<T extends {
  _id?: string;
  id?: string;
  client?: AppointmentParty;
  stylist?: AppointmentParty;
  service?: AppointmentParty;
  status?: string;
}>(existing: T, event: AppointmentUpdateEvent): T {
  return mergeAppointmentRecord(existing, event.appointment as Partial<T> | undefined, event.status);
}

export function patchAppointmentList<T extends { _id?: string; id?: string; status?: string }>(
  list: T[],
  event: AppointmentUpdateEvent,
  options?: { appendMissing?: boolean }
): T[] {
  const eventId = eventAppointmentId(event);
  if (!eventId) return list;
  let found = false;
  const next = list.map((item) => {
    if (appointmentKey(item) !== eventId) return item;
    found = true;
    return mergeAppointmentUpdate(item, event);
  });
  if (!found && options?.appendMissing && event.appointment) {
    next.push(mergeAppointmentUpdate({} as T, event));
  }
  return next;
}

export function patchAppointmentGroups<T extends { _id?: string; id?: string; status?: string }>(
  groups: { [key: string]: T[] },
  event: AppointmentUpdateEvent
): { [key: string]: T[] } {
  const next: { [key: string]: T[] } = {};
  Object.keys(groups).forEach((key) => {
    next[key] = patchAppointmentList(groups[key], event);
  });
  return next;
}
